import React, { useCallback, useEffect, useRef, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import SecurityApi from "../../services/SecurityApi";
import GroupApi from "../../services/GroupApi";
import PlantApi from "../../services/PlantApi";
import PlantUserApi from "../../services/PlantUserApi";
import { formatPhone, isValidPhone, sanitizePhone } from "../../utilities/phone";

// Design System
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import Input from "../../components/Input/Input";
import FormCard from "../../components/FormCard/FormCard";

type GroupOption = {
  id: string;
  name: string;
  color?: string;
};

type PlantOption = {
  id: string;
  name: string;
  isActive?: boolean;
};

type DropdownTemplateProps = {
  placeholder?: string;
};

const extractList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.Items)) return payload.Items;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const normalizePlantOption = (entry: any): PlantOption | null => {
  const id = entry?.id ?? entry?.plantId ?? entry?.Id ?? entry?.PlantId ?? "";
  if (!id) {
    return null;
  }

  const name = entry?.name ?? entry?.plantName ?? entry?.Name ?? id;
  const status = entry?.active ?? entry?.isActive ?? entry?.Active ?? entry?.status;
  const isActive = typeof status === "string" ? status.toLowerCase() === "active" : status;

  return {
    id,
    name: name || id,
    isActive: typeof isActive === "boolean" ? isActive : undefined,
  };
};

const resolveCreatedUserId = (payload: any): string => {
  if (!payload) {
    return "";
  }

  const candidates: Array<string | number | undefined> = [
    payload?.id,
    payload?.Id,
    payload?.userId,
    payload?.UserId,
    payload?.data?.id,
    payload?.data?.Id,
    payload?.data?.userId,
    payload?.data?.UserId,
  ];

  const match = candidates.find((value) => {
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    if (typeof value === "number") {
      return Number.isFinite(value);
    }
    return false;
  });

  if (typeof match === "number") {
    return String(match);
  }

  return match ? match.trim() : "";
};

const CreateUserForm: React.FC = () => {
  const [name, setName] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [departament, setDepartament] = useState("");
  const [position, setPosition] = useState("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [plants, setPlants] = useState<PlantOption[]>([]);
  const [plantsLoading, setPlantsLoading] = useState(false);
  const [selectedPlantId, setSelectedPlantId] = useState<string>("");
  const [error, setError] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const groupIdValue = selectedGroup?.id ?? "";
  const plantIdValue = selectedPlantId?.trim() ?? "";

  const { displayToast } = useToast();
  const { companyId } = useAuth();
  const navigate = useNavigate();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");

  const extractErrorMessage = useCallback((error: any): string => {
    if (!error) {
      return "";
    }

    const candidates: any[] = [
      error?.data?.error?.message,
      error?.data?.message,
      Array.isArray(error?.errors)
        ? error.errors.find((msg: any) => typeof msg === "string" && msg.trim().length > 0)
        : null,
      error?.response?.data?.error?.message,
      error?.response?.data?.message,
      error?.message,
    ];

    const match = candidates.find((entry) => typeof entry === "string" && entry.trim().length > 0);
    return match ? match.trim() : "";
  }, []);

  const showErrorToast = useCallback(
    (error: any, duration = 3000, fallbackDetail?: string): void => {
      const summary = translateMessages("error") || "Erro";
      const detail = extractErrorMessage(error) || fallbackDetail || summary;
      displayToast("error", summary, detail, false, duration);
    },
    [displayToast, extractErrorMessage, translateMessages],
  );

  const nameRef = useRef<HTMLInputElement | null>(null);
  const userNameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<any>(null);
  const phoneRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);
  const departamentRef = useRef<any>(null);
  const positionRef = useRef<any>(null);
  const plantIdRef = useRef<any>(null);
  const groupIdRef = useRef<any>(null);

  const isValidEmail = (val: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const isValidGuid = (val: string): boolean =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(val.trim());

  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      try {
        setGroupsLoading(true);
        const response = await GroupApi.ListGroups();
        const list: GroupOption[] = Array.isArray(response?.data)
          ? (response.data as GroupOption[])
          : [];
        setGroups(list);
      } catch (err) {
        showErrorToast(err, 3000, translateMessages("groupsLoadFailed") || "Falha ao carregar grupos");
      } finally {
        setGroupsLoading(false);
      }
    };

    loadGroups();
  }, [showErrorToast, translateMessages]);

  useEffect(() => {
    const companyIdFromToken = (companyId ?? "").trim();

    if (!companyIdFromToken) {
      setPlants([]);
      setSelectedPlantId("");
      displayToast(
        "error",
        translateMessages("error") || "Erro",
        translateForm("CompanyNotFound") || translateForm("companyNotFound") || "Empresa não encontrada para o usuário logado.",
        false,
        4000,
      );
      return;
    }

    const loadPlants = async (): Promise<void> => {
      try {
        setPlantsLoading(true);
        const response = await PlantApi.GetPlantsByCompanyId(companyIdFromToken);
        const rawPayload = response?.data ?? response;
        const normalized = extractList(rawPayload)
          .map((item) => normalizePlantOption(item))
          .filter((entry): entry is PlantOption => Boolean(entry))
          .filter((entry) => entry.isActive !== false);

        setPlants(normalized);
        setSelectedPlantId((prev) => {
          if (normalized.length === 1) {
            return normalized[0].id;
          }

          if (normalized.some((item) => item.id === prev)) {
            return prev;
          }

          return "";
        });

        if (normalized.length === 0) {
          displayToast(
            "warn",
            translateMessages("attention") || translateForm("attention") || "Atenção",
            translateForm("noPlantsFound") || "Nenhuma planta disponível para seleção.",
            false,
            4000,
          );
        }
      } catch (err) {
        showErrorToast(err, 4000, translateForm("plantsLoadFailed") || "Falha ao carregar plantas");
        setPlants([]);
        setSelectedPlantId("");
      } finally {
        setPlantsLoading(false);
      }
    };

    loadPlants();
  }, [companyId, displayToast, showErrorToast, translateForm, translateMessages]);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedUserName = userName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const sanitizedPhone = sanitizePhone(trimmedPhone);
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const trimmedPlantId = plantIdValue;

    const hasEmpty =
      !trimmedName ||
      !trimmedUserName ||
      !trimmedEmail ||
      !trimmedPassword ||
      !trimmedConfirmPassword ||
      !trimmedPlantId;

    if (hasEmpty) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("requiredFields"),
        false,
        3000,
      );
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidEmail") || "E-mail inválido",
        false,
        3000,
      );
      emailRef.current?.focus();
      return;
    }

    if (sanitizedPhone && !isValidPhone(trimmedPhone)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidPhone") || translateForm("invalidPhone") || "Telefone inválido",
        false,
        3000,
      );
      phoneRef.current?.focus();
      return;
    }

    if (!trimmedPlantId || !isValidGuid(trimmedPlantId)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateForm("invalidPlant") || translateMessages("invalidPlant") || "Planta inválida",
        false,
        3000,
      );
      plantIdRef.current?.focus?.();
      return;
    }

    if (password !== confirmPassword) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("passwordsDoNotMatch") || "Senhas não conferem",
        false,
        3000,
      );
      confirmPasswordRef.current?.focus();
      return;
    }

    if (!groupIdValue || !isValidGuid(groupIdValue)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidGroup") || "Grupo inválido",
        false,
        3000,
      );
      groupIdRef.current?.focus?.();
      return;
    }

    try {
      const signinResult = await SecurityApi.Signin({
        Name: trimmedName,
        Username: trimmedUserName,
        Email: trimmedEmail,
  Phone: sanitizedPhone,
        Password: password,
        ConfirmPassword: confirmPassword,
        Departament: departament.trim() ? departament.trim() : null,
        Position: position.trim() ? position.trim() : null,
        GroupId: groupIdValue,
        Active: isActive,
        PlantId: trimmedPlantId,
      });

      if (!signinResult?.success) {
        const detail = Array.isArray(signinResult?.errors) && signinResult.errors.length > 0
          ? signinResult.errors[0]
          : translateForm("userNotCreated") || "Não foi possível criar o usuário";
        displayToast(
          "error",
          translateMessages("error"),
          detail,
          false,
          4000,
        );
        return;
      }

      const createdUserId = resolveCreatedUserId(signinResult?.data);
      if (createdUserId) {
        try {
          await PlantUserApi.AssignPlants([
            { userId: createdUserId, plantId: trimmedPlantId, isDefault: true },
          ]);
        } catch (linkErr) {
          showErrorToast(linkErr, 4000, translateForm("userPlantLinkFailed") || "Planta não vinculada ao usuário");
        }
      }

      displayToast(
        "success",
        translateForm("Success"),
        translateForm("userCreated") || "Usuário criado com sucesso",
        false,
        3000,
      );

      setName("");
      setUserName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setDepartament("");
      setPosition("");
      setSelectedGroup(null);
      setSelectedPlantId(plants.length === 1 ? plants[0].id : "");
      setIsActive(true);
      setError(false);

      setTimeout(() => {
        navigate("/users");
      }, 1000);
    } catch (err: any) {
      showErrorToast(err, 3000, translateForm("userNotCreated") || "Não foi possível criar o usuário");
    }
  };

  const renderDropdownValueTemplate = (
    option: GroupOption | null,
    props: DropdownTemplateProps,
  ): React.ReactNode => {
    const resolvedOption = option ?? (groupIdValue ? groups.find((item) => item.id === groupIdValue) ?? null : null);

    if (!resolvedOption) {
      return <span>{props.placeholder}</span>;
    }

    return (
      <div className="flex align-items-center gap-2">
        {resolvedOption.color && (
          <span
            style={{
              width: 12,
              height: 12,
              backgroundColor: resolvedOption.color,
              display: "inline-block",
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.2)",
            }}
          />
        )}
        <span>{resolvedOption.name}</span>
      </div>
    );
  };

  const renderDropdownItemTemplate = (option: GroupOption): React.ReactNode => (
    <div className="flex align-items-center gap-2">
      {option.color && (
        <span
          style={{
            width: 12,
            height: 12,
            backgroundColor: option.color,
            display: "inline-block",
            borderRadius: "50%",
            border: "1px solid rgba(0,0,0,0.2)",
          }}
        />
      )}
      <span>{option.name}</span>
    </div>
  );



  return (
    <>
      <FormCard
        title={translateForm("createUser") || "Cadastrar Usuário"}
        icon="person_add"
        onSubmit={handleSubmit}
        onCancel={() => setShowConfirmCancel(true)}
        cancelButtonLabel={translateForm("back") || "Voltar"}
        submitButtonLabel={translateForm("register") || "Cadastrar"}
        numColumns={12}
      >
      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="name"
          ref={nameRef}
          type="text"
          label={translateForm("name") || "Nome"}
          value={name}
          onChange={(val) => { setName(val); setError(false); }}
          required
          className={error && !name.trim() ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="userName"
          ref={userNameRef}
          type="text"
          label={translateForm("userName") || "Usuário"}
          value={userName}
          onChange={(val) => { setUserName(val); setError(false); }}
          required
          className={error && !userName.trim() ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="email"
          ref={emailRef}
          type="text"
          label={translateForm("email") || "E-mail"}
          value={email}
          onChange={(val) => { setEmail(val); setError(false); }}
          required
          className={error && (!email.trim() || !isValidEmail(email)) ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="phone"
          ref={phoneRef}
          type="text"
          label={translateForm("phone") || "Telefone"}
          value={phone}
          onChange={(val) => { setPhone(formatPhone(val)); setError(false); }}
          className={error && Boolean(phone.trim()) && !isValidPhone(phone.trim()) ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="password"
          ref={passwordRef}
          type="password"
          label={translateForm("password") || "Senha"}
          value={password}
          onChange={(val) => { setPassword(val || ""); setError(false); }}
          required
          className={error && !password.trim() ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="confirmPassword"
          ref={confirmPasswordRef}
          type="password"
          label={translateForm("confirmPassword") || "Confirmar Senha"}
          value={confirmPassword}
          onChange={(val) => { setConfirmPassword(val || ""); setError(false); }}
          required
          className={error && (!confirmPassword.trim() || confirmPassword !== password) ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="departament"
          ref={departamentRef}
          type="text"
          label={translateForm("departament") || "Departamento"}
          value={departament}
          onChange={(val) => { setDepartament(val); setError(false); }}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="position"
          ref={positionRef}
          type="text"
          label={translateForm("position") || "Cargo"}
          value={position}
          onChange={(val) => { setPosition(val); setError(false); }}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="plantId"
          ref={plantIdRef}
          type="select"
          label={translateForm("plant") || translateForm("selectPlant") || "Planta"}
          value={plantIdValue}
          options={plants}
          optionLabel="name"
          optionValue="id"
          onChange={(val) => { setSelectedPlantId(val); setError(false); }}
          required
          loading={plantsLoading}
          disabled={plantsLoading || plants.length === 0}
          className={error && (!plantIdValue || !isValidGuid(plantIdValue)) ? "p-invalid border-red" : ""}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          id="groupId"
          ref={groupIdRef}
          type="select"
          label={translateForm("group") || "Grupo"}
          value={groupIdValue}
          options={groups}
          optionLabel="name"
          optionValue="id"
          onChange={(val) => { 
            const match = groups.find((item) => item.id === val) ?? null;
            setSelectedGroup(match);
            setError(false);
          }}
          required
          valueTemplate={renderDropdownValueTemplate}
          itemTemplate={renderDropdownItemTemplate}
          loading={groupsLoading}
          disabled={groupsLoading}
          className={error && (!groupIdValue || !isValidGuid(groupIdValue)) ? "p-invalid border-red" : ""}
          filter
        />
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="isActive"
            checked={isActive}
            onChange={(e) => {
              setIsActive(Boolean(e.checked));
              setError(false);
            }}
          />
          <label htmlFor="isActive" className="text-sm m-0">
            {translateForm("active") || translateMessages("active") || "Ativo"}
          </label>
        </div>
      </div>
    </FormCard>

    <ConfirmModal
      visible={showConfirmCancel}
      onHide={() => setShowConfirmCancel(false)}
      onConfirm={() => navigate("/users")}
    />
    </>
  );
};

export default CreateUserForm;
